import { Button, Card, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type {
	OutstandingBalanceParameters,
	OutstandingBalanceReportFormat,
	OutstandingBalanceSummaryParameters,
	OutstandingBalanceSummaryReportFormat,
} from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

const initialAsOfDate: Dayjs = dayjs();

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

type OutstandingBalanceReportType = 'outstandingBalance' | 'outstandingBalanceSummary';
type OutstandingBalanceReportParameters = OutstandingBalanceParameters | OutstandingBalanceSummaryParameters;

const addHierarchyIds = (
	parameters: OutstandingBalanceReportParameters,
	key: 'areaIds' | 'branchIds' | 'districtIds',
	ids: number[],
) => {
	if (ids.length > 0) {
		parameters[key] = ids;
	}
};

const addFacilityIds = (parameters: OutstandingBalanceReportParameters, facilityIds: string[]) => {
	if (facilityIds.length > 0) {
		parameters.facilityIds = facilityIds;
	}
};

const OutstandingBalanceReportFormBase = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
	reportType,
}: ReportFormProps & { reportType: OutstandingBalanceReportType }) => {
	const [asOfDate, setAsOfDate] = useState<Dayjs | null>(initialAsOfDate);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [currentBalances, setCurrentBalances] = useState(true);
	const [subscriptionStatus, setSubscriptionStatus] = useState(false);
	const [contactInfo, setContactInfo] = useState(false);
	const [format, setFormat] = useState<OutstandingBalanceReportFormat | OutstandingBalanceSummaryReportFormat>('csv');
	const isSummaryReport = reportType === 'outstandingBalanceSummary';

	const canSubmit = useMemo(() => {
		return Boolean(asOfDate);
	}, [asOfDate]);

	const reset = () => {
		setAsOfDate(initialAsOfDate);
		setAccountFilter({ type: 'all' });
		setFacilityHierarchy(initialFacilityHierarchy);
		setCurrentBalances(true);
		setSubscriptionStatus(false);
		setContactInfo(false);
		setFormat('csv');
	};

	const buildAccountFilter = (): OutstandingBalanceReportParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): OutstandingBalanceReportParameters => {
		if (!asOfDate) {
			throw new Error('Select an as-of date.');
		}

		const accountFilter = buildAccountFilter();
		// The PDF option is only offered in the UI for the summary report, so `format` is
		// guaranteed to be a valid OutstandingBalanceReportFormat when reportType is 'outstandingBalance'.
		const parameters: OutstandingBalanceReportParameters =
			reportType === 'outstandingBalance'
				? {
						asOfDate: asOfDate.format('YYYY-MM-DD'),
						currentBalances,
						format: format as OutstandingBalanceReportFormat,
					}
				: {
						asOfDate: asOfDate.format('YYYY-MM-DD'),
						contactInfo,
						currentBalances,
						format: format as OutstandingBalanceSummaryReportFormat,
						subscriptionStatus,
					};

		if (accountFilter) {
			parameters.accountFilter = accountFilter;
		}

		addHierarchyIds(
			parameters,
			'areaIds',
			facilityHierarchy.areas.map((area) => area.id),
		);

		addHierarchyIds(
			parameters,
			'branchIds',
			facilityHierarchy.branches.map((branch) => branch.id),
		);

		addHierarchyIds(
			parameters,
			'districtIds',
			facilityHierarchy.districts.map((district) => district.id),
		);

		addFacilityIds(
			parameters,
			facilityHierarchy.facilities.map((facility) => facility.id),
		);

		if (facilityHierarchy.facilities.length === 1) {
			const facility = facilityHierarchy.facilities[0];
			const facilityNumber = facility.facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}

			if (facility.facilityName) {
				parameters.facilityName = facility.facilityName;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (!asOfDate) {
			onValidationError('Select an as-of date.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType,
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker<Dayjs> label='As of date' value={asOfDate} onChange={setAsOfDate} />
				</LocalizationProvider>

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<Stack>
					<FormControlLabel
						control={
							<Checkbox
								checked={currentBalances}
								onChange={(event) => setCurrentBalances(event.target.checked)}
							/>
						}
						label='Include current balances'
					/>

					{isSummaryReport && (
						<>
							<FormControlLabel
								control={
									<Checkbox
										checked={subscriptionStatus}
										onChange={(event) => setSubscriptionStatus(event.target.checked)}
									/>
								}
								label='Include subscription status'
							/>

							<FormControlLabel
								control={
									<Checkbox
										checked={contactInfo}
										onChange={(event) => setContactInfo(event.target.checked)}
									/>
								}
								label='Include contact information'
							/>
						</>
					)}
				</Stack>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(
							_,
							nextFormat: OutstandingBalanceReportFormat | OutstandingBalanceSummaryReportFormat | null,
						) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						{isSummaryReport && <ToggleButton value='pdf'>PDF</ToggleButton>}
						<ToggleButton value='xlsx'>XLSX</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};

export const OutstandingBalanceReportForm = (props: ReportFormProps) => (
	<OutstandingBalanceReportFormBase {...props} reportType='outstandingBalance' />
);

export const OutstandingBalanceSummaryReportForm = (props: ReportFormProps) => (
	<OutstandingBalanceReportFormBase {...props} reportType='outstandingBalanceSummary' />
);
